import type {SyntheticEvent} from 'react'
import React, {forwardRef} from 'react'
import {useOutletContext, useRouteLoaderData, useSubmit} from '@remix-run/react'

import clsx from 'clsx'

import {useReportItemSeen, useReportWidgetClick, useSendPartnerProductClickout} from '~/hooks/analytic-hooks'
import {objectToQueryString} from '~/utils/commons'
import type {RelatedProduct, RootAndListContext, RootLoaderResponse} from '~/types'
import {longWordsLanguages, getTranslation} from '~/localization/translation'
import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'
import {getDomainConfig} from '~/domains'
import {grantAllConsent} from '~/utils/consent-manager.client'
import {DiscountBadge} from '../badges/discount-badge'

export interface ProductCardProps {
  product: RelatedProduct
  index: number
}

export const DwsRelatedProductCard = forwardRef<HTMLDivElement, ProductCardProps>(
  ({product, index}, dwsRelatedProductCardRef) => {
    const {image, name, affiliateLink, discount, brand, asin, isSellerProduct, price} = product

    const {locale, language, featureFlags, trackAnalytics, domain} = useRouteLoaderData('root') as RootLoaderResponse
    const {
      dataResponse: {productsCategory},
      clickId,
      setShowCookieConsentGdpr,
    } = useOutletContext<RootAndListContext>()
    const {styles: stylesBydomain} = getDomainConfig(domain, locale)

    const translations = getTranslation(language)
    const productDiscount = Math.max(product.discount || 0, product.coupon?.value || 0)
    const shouldForceTrackAnalytics = Boolean(
      featureFlags?.cookieConsentVariation === 'opt-in' &&
      (!trackAnalytics?.permission || trackAnalytics?.permission === 'denied'),
    )
    const trackSearchParams = objectToQueryString({
      session_id: trackAnalytics?.session_id,
      ...trackAnalytics?.pageLoadData,
      page_id: trackAnalytics?.page_id,
      user_id: trackAnalytics?.user_id,
    })

    const affiliateLinkWithClickId = affiliateLink.url.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

    const redirectUrl = shouldForceTrackAnalytics
      ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
      : affiliateLinkWithClickId

    const submit = useSubmit()

    const reportCardClick = useReportWidgetClick()
    const reportClickout = useSendPartnerProductClickout({
      product,
      index,
      locale,
      listName: 'related-products',
      categorySlug: productsCategory?.slug,
      categoryName: productsCategory?.name,
    })
    const [cardRef] = useReportItemSeen(
      {
        item_list_id: productsCategory?.slug,
        item_name: name,
        item_source: 'widget',
        partner_item_brand: brand || '',
        partner_item_id: asin,
        affiliation: affiliateLink.type,
        is_dws: isSellerProduct,
        price: price?.amount,
        currency: price?.currency,
        click_element_name: 'related-product',
        index,
        variation: '',
      },
      {},
    )

    const handleCardClick = (_e: SyntheticEvent) => {
      if (shouldForceTrackAnalytics) {
        setShowCookieConsentGdpr(false)
        grantAllConsent()
        submit(
          {
            'accept-gdpr': 'granted',
            redirect: 'true',
            'track-analytics-data': JSON.stringify({...trackAnalytics, permission: 'granted'}),
          },
          {method: 'POST', action: '/cookie-consent', replace: false, navigate: false},
        )
      }

      reportClickout()
      reportCardClick({
        widget_name: 'related-product-card',
        widget_place: 'middle',
        variation: '',
      })

      if (typeof Storage != 'undefined') {
        localStorage.setItem('clickTime', Date.now().toString())
        if (featureFlags?.showHustonProblemPopup) localStorage.setItem('redirectUrl', redirectUrl)
      }
    }

    return (
      <div ref={dwsRelatedProductCardRef} data-testid={`related-product-card-${index}`}>
        <div
          id="dws-related-product-card"
          className="relative mr-4 flex h-[270px] min-w-[190px] max-w-[190px] flex-col items-center rounded-lg bg-white px-3 py-2"
        >
          {discount ? (
            <DiscountBadge
              discount={productDiscount}
              className={clsx(
                'absolute left-2 top-2 h-auto rounded-2xl px-2 py-1 text-sm',
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_OLD_BG,
                stylesBydomain.LISTPAGE_BADGE_CARD_DISCOUNT_OLD_TEXT,
                'listpage_badge_card_discount_old_bg listpage_badge_card_discount_old_text',
              )}
              source="related-list"
            />
          ) : null}

          <div className="flex h-full w-full flex-col items-center py-2" ref={cardRef}>
            <div className="my-2 h-[122px] w-[122px]">
              <a
                aria-label={`view ${brand}`}
                href={redirectUrl}
                onClick={handleCardClick}
                rel="nofollow noreferrer"
                target="_blank"
              >
                <img className="h-[122px] w-[122px] object-contain" src={image} alt={name} loading="lazy" />
              </a>
            </div>
            <div className="z-20 mt-auto">
              <a
                aria-label={`view ${brand}`}
                href={redirectUrl}
                onClick={handleCardClick}
                rel="nofollow noreferrer"
                target="_blank"
              >
                <p className="mb-3 line-clamp-2 w-full leading-6">
                  {name?.replace(/[|/]/g, ' ')?.replace(/,\s*/g, ', ')}
                </p>
              </a>

              <a
                href={redirectUrl}
                onClick={handleCardClick}
                rel="nofollow noreferrer"
                target="_blank"
                aria-label={translations.ProductCard.ctaText('dws')}
                className={clsx(
                  'mx-auto flex min-w-44 cursor-pointer justify-center rounded-[2rem] border bg-white px-2 py-2 uppercase',
                  longWordsLanguages.includes(language) ? 'text-sm' : 'text-base',
                  `${stylesBydomain.LISTPAGE_BUTTON_SECONDARY} listpage_button_secondary`,
                )}
              >
                {translations.ProductCard.ctaText('dws', undefined)}
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  },
)
